import {
    ContractContextProvider,
    useContract,
    useContractContext,
} from 'components/contract/contractContext';
import { usePageLangauge } from 'components/contractLanguagePage/contractLanguagePage';
import {
    CollectionProvider,
    CollectionState,
    useCollectionContext,
} from 'components/settings/collectionContext';
import { DocumentProvider } from 'components/settings/documentContext';
import { TranslationsDisplay } from 'components/translationsPage/translationsPage';
import { where } from 'firebase/firestore';
import {
    clauseConverter,
    CLAUSE_COLLECTION_NAME,
    contractConverter,
    CONTRACT_COLLECTION_NAME,
    coverageConverter,
    COVERAGE_COLLECTION_NAME,
    languageConverter,
    LANGUAGE_COLLECTION_NAME,
    sectionConverter,
    SECTION_COLLECTION_NAME,
    subsectionConverter,
    SUBSECTION_COLLECTION_NAME,
    Translation,
    TranslationTarget,
} from 'models';
import { useParams } from 'react-router-dom';
import './contractTranslationPage.css';

export const ContractTranslationPage = () => {
    const { contractId, languageId } = useParams();
    if (!contractId) {
        return <div>`Cannot load contract with id '${contractId}'`</div>;
    }
    if (!languageId) {
        return <div>`Cannot load languageId with id '${languageId}'`</div>;
    }
    return (
        <DocumentProvider
            collectionName={CONTRACT_COLLECTION_NAME}
            converter={contractConverter}
            id={contractId}
        >
            <DocumentProvider
                collectionName={LANGUAGE_COLLECTION_NAME}
                converter={languageConverter}
                id={languageId}
            >
                <ContractTranslationPageWrapper />
            </DocumentProvider>
        </DocumentProvider>
    );
};

const ContractTranslationPageWrapper = () => {
    const [contract, contractPending] = useContract();
    const [language, languagePenging, , translationsProps] = usePageLangauge();
    if (contractPending) {
        return <div>Loading contract</div>;
    }
    if (languagePenging) {
        return <div>Loading language</div>;
    }
    if (!contract) {
        return <div>Could not load contract</div>;
    }
    if (!language) {
        return <div>Could not load language</div>;
    }
    return (
        <CollectionProvider {...translationsProps}>
            <CollectionProvider
                collectionName={SECTION_COLLECTION_NAME}
                converter={sectionConverter}
            >
                <CollectionProvider
                    collectionName={SUBSECTION_COLLECTION_NAME}
                    converter={subsectionConverter}
                >
                    <CollectionProvider
                        collectionName={COVERAGE_COLLECTION_NAME}
                        converter={coverageConverter}
                        constraints={[
                            where(
                                'productTypeIndexIds',
                                'array-contains',
                                contract.productTypeIndexId
                            ),
                        ]}
                    >
                        <CollectionProvider
                            collectionName={CLAUSE_COLLECTION_NAME}
                            converter={clauseConverter}
                            constraints={[
                                where(
                                    'productTypeIndexIds',
                                    'array-contains',
                                    contract.productTypeIndexId
                                ),
                            ]}
                        >
                            <ContractContextProvider>
                                <ContractTranslationPageContent />
                            </ContractContextProvider>
                        </CollectionProvider>
                    </CollectionProvider>
                </CollectionProvider>
            </CollectionProvider>
        </CollectionProvider>
    );
};

const ContractTranslationPageContent = () => {
    const { contract } = useContractContext();
    const [language, , , translationsProps] = usePageLangauge();
    const [, , , sectionMap] = useCollectionContext(SECTION_COLLECTION_NAME);
    const [, , , subsectionMap] = useCollectionContext(SUBSECTION_COLLECTION_NAME);
    const [, , , coverageMap] = useCollectionContext(COVERAGE_COLLECTION_NAME, [
        where('productTypeIndexIds', 'array-contains', contract.productTypeIndexId),
    ]);
    const [, , , clauseMap] = useCollectionContext(CLAUSE_COLLECTION_NAME, [
        where('productTypeIndexIds', 'array-contains', contract.productTypeIndexId),
    ]);
    const [translations] = useCollectionContext(
        translationsProps.collectionName,
        translationsProps.constraints
    ) as CollectionState<Translation>;
    const records = contract
        .getIndexIds()
        .map((indexId) => {
            return (
                sectionMap.get(indexId) ||
                subsectionMap.get(indexId) ||
                coverageMap.get(indexId) ||
                clauseMap.get(indexId)
            );
        })
        .filter((record) => !!record) as TranslationTarget[];
    return (
        <div style={{ padding: '0 70px 70px 70px' }}>
            <div style={{ fontSize: '34px', display: 'flex', justifyContent: 'center' }}>
                Contract Translations
            </div>
            <div style={{ marginBottom: '20px' }}>
                <div style={{ fontSize: '24px' }}> {contract.name}</div>
            </div>
            <div style={{ marginBottom: '20px' }}>
                <div style={{ fontSize: '24px' }}> {language?.name}</div>
            </div>
            <TranslationsDisplay records={records} translations={translations || []} />
        </div>
    );
};
